import React, { useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { deletePopup, fetchPopups } from "shared/api/PopupApi";
import { applyDateFilter, isToday } from "shared/lib/date/dateFilters";
import { formatToKST, toKstDate } from "shared/lib/date/formatKst";
import { ActionButton, EditButton } from "shared/ui/buttons";
import { alertDialog, confirmDialog } from "shared/ui/feedback";
import { SearchPanel } from "shared/ui/filters";
import { AddHeader, AlertDiv, BaseContainer } from "shared/ui/layout";
import {
  DataTable,
  Pagination,
  SelectAllToggle,
  useClientPagination,
  useTableSelection,
} from "shared/ui/table";

// createdAt은 서버가 UTC로 생성하므로 비교·표시 모두 KST 보정해서 다룬다.
const toLocalDate = (value) => toKstDate(value);

// 한국(KST) 기준으로 표시
const formatKoreanDateTime = (value) => formatToKST(value);

// 구분 칩(value) → 파생 상태 문자열 매핑.
const STATUS_VALUE_TO_STATE = {
  wait: "예약",
  pub: "게시",
  unpub: "게시 종료",
};

// 서버가 hide(숨김 여부)와 progressStatus(BEFORE/ACTIVE/ENDED)를 내려준다.
// progressStatus는 서버가 start·end와 현재 시각을 비교해 매 조회 시 계산한 값이다.
//  - hide=true(미게시): 관리자가 명시적으로 숨긴 상태 → 최우선.
//  - progressStatus ENDED: 게시 종료.
//  - progressStatus BEFORE: 예약(시작 전).
//  - progressStatus ACTIVE(또는 상시): 게시.
const derivePopupStatus = (popup) => {
  if (popup?.hide) return "미게시";
  if (popup?.progressStatus === "ENDED") return "게시 종료";
  if (popup?.progressStatus === "BEFORE") return "예약";
  return "게시";
};

const ActionGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`;
const DataDiv = styled.div`
  width: 100%;
`;

const P = styled.span`
  color: #0079e0;
  font-weight: bold;
`;

const EditBtn = styled(EditButton)`
  width: fit-content;
  height: fit-content;
  padding: 5px 10px;
`;

function PopupManager() {
  const [queriedpopupData, setQueriedpopupData] = useState([]);
  const [isSearched, setIsSearched] = useState(false);
  const [, setDeletionInProgress] = useState(false);
  const [popupData, setpopupData] = useState([]);
  const navigate = useNavigate();

  const itemsPerPageposting = 9;

  const sourceData = isSearched ? queriedpopupData : popupData;
  const selection = useTableSelection(sourceData);
  const { pageItems, page, setPage, pageSize, totalCount } =
    useClientPagination(sourceData, { pageSize: itemsPerPageposting });

  // 오늘 등록된 팝업 수 (createdAt을 KST 벽시계 그대로 파싱해 비교)
  const newPostsCount = useMemo(
    () =>
      popupData.filter((item) => {
        const postDate = toLocalDate(item.createdAt);
        return postDate ? isToday(postDate) : false;
      }).length,
    [popupData],
  );

  const handleEndButtonClick = async () => {
    if (selection.count > 0) {
      const shouldDelete = await confirmDialog(
        "해당 팝업을 삭제하시겠습니까?\n삭제시 정보가 사라집니다. 그래도 삭제하시겠습니까?",
      );
      if (shouldDelete) {
        // Handle the deletion
        handledeletedClick();
      }
    } else {
      const shouldContinue = alertDialog(
        "삭제할 팝업이 선택되지 않았습니다.\n삭제를 희망하시는 팝업을 선택 후 삭제해주세요. "
        
      );
      if (!shouldContinue) {
        // User chose not to continue
        return;
      }
      // Handle other actions or display a message
    }
  };

  const ReadBanner = async () => {
    try {
      const popUps = await fetchPopups();
      setpopupData(popUps.slice().reverse());
    } catch (error) {
    }
  };
  useEffect(() => {
    ReadBanner();
  }, []);

  const handleButtonClick = () => {
    navigate("/main/popup/register");
  };

  // 검색: 제목·구분·기간(빠른 칩 또는 직접 범위)을 공용 applyDateFilter로 처리.
  const handleSearchFilters = (filters) => {
    const { keyword, chips, dateButtonClicked, startDate, endDate } = filters;
    const status = chips?.status ?? "all";

    let queried = popupData.filter((popup) => {
      const includesSearchTerm = popup.title?.includes(keyword) ?? false;
      // 게시기간 기반 파생 상태로 필터(표의 상태 열과 동일 기준).
      const matchesStatus =
        status === "all" ||
        derivePopupStatus(popup) === STATUS_VALUE_TO_STATE[status];
      return includesSearchTerm && matchesStatus;
    });

    queried = applyDateFilter({
      data: queried,
      dateButtonClicked,
      startDate,
      endDate,
      getDate: (popup) => toLocalDate(popup.createdAt) ?? new Date(NaN),
    });

    if (queried.length === 0) {
      alertDialog(
        "해당 검색어를 찾을 수 없습니다.\n검색어를 다시 확인하시고 검색해주세요.",
      );
    }
    setQueriedpopupData(queried);
    setIsSearched(true);
  };

  const handleResetFilters = () => {
    setQueriedpopupData([]);
    setIsSearched(false);
    setPage(1);
  };

  const columns = useMemo(
    () => [
      { Header: "No.", accessor: "id" },
      {
        Header: "이미지",
        accessor: "image",
        Cell: ({ value }) =>
          value ? (
            <img
              src={value}
              alt="팝업 미리보기"
              style={{ maxWidth: "500px", maxHeight: "50px" }}
            />
          ) : (
            <span>-</span>
          ),
      },
      {
        Header: "팝업 타이틀",
        accessor: "title",
        Cell: ({ value }) => {
          // Truncate the title to a maximum of 22 characters
          const text = value ?? "";
          const truncatedTitle =
            text.length > 22 ? text.substring(0, 22) + "..." : text;

          return (
            <span title={text} style={{ fontFamily: 'monospace' }}>
              {truncatedTitle}
            </span>
          );
        },
      },
      {
        Header: "게시 기간",
        id: "postPeriod",
        accessor: (d) => `${d.start ?? ""} ~ ${d.end ?? ""}`,
        Cell: ({ row }) => {
          const { start, end } = row.original;
          // 기간 미설정(상시 게시)일 때 'null ~ null' 대신 '상시'로 표시
          if (!start && !end) return "상시";
          return `${start ?? "-"} ~ ${end ?? "-"}`;
        },
      },
      {
        Header: "등록일",
        accessor: "createdAt",
        Cell: ({ value }) => formatKoreanDateTime(value),
      },
      {
        Header: "상태",
        accessor: "status",
        // 게시기간 기반 파생 상태로 표시(필터 '구분'과 동일 기준).
        Cell: ({ row }) => derivePopupStatus(row.original),
      },
      {
        Header: "설정",
        id: "manage",
        accessor: (row) => row.id,
        Cell: ({ row }) => (
          <EditBtn
            onClick={() => navigate(`/main/popup/edit/${row.original.id}`)}
          >
            설정
          </EditBtn>
        ),
      },
    ],
    [navigate]
  );
  const handledeletedClick = async () => {
    try {
      setDeletionInProgress(true);
      const selectedRowsCopy = [...selection.selectedRows];

      for (const row of selectedRowsCopy) {
        try {
          await deletePopup(row.id);
          // Update popupData by removing the deleted item
          setpopupData((prevData) =>
            prevData.filter((popup) => popup.id !== row.id)
          );
        } catch (error) {
          // Handle individual deletion error
          // console.error("An error occurred while deleting the item:", error);
        }
      }

      selection.clear(); // Clear the selection
      //setdeletedShowModal(false); // Close the modal
      navigate("/main/popup");
      setDeletionInProgress(false);
    } catch (error) {
      alertDialog("An error occurred while deleting the items. Please try again.");
      /*
      console.error(
        "An error occurred in the handledeletedClick function:",
        error
      );
      */
      setDeletionInProgress(false);
    }
  };

  const POPUP_STATUS_OPTIONS = [
    { label: "전체", value: "all" },
    { label: "예약", value: "wait" },
    { label: "게시", value: "pub" },
    { label: "게시 종료", value: "unpub" },
  ];
  const POPUP_QUICK_RANGE_OPTIONS = [
    { label: "오늘", value: 0 },
    { label: "1주일", value: 1 },
    { label: "1개월", value: 2 },
    { label: "3개월", value: 3 },
    { label: "6개월", value: 4 },
    { label: "1년", value: 5 },
  ];
  return (
    <BaseContainer>
      <AddHeader
        pageName="팝업 관리"
        onClick={handleButtonClick}
        buttonValue="팝업 등록하기"
      />

      <SearchPanel onSearch={handleSearchFilters} onReset={handleResetFilters}>
        <SearchPanel.Keyword label="조건 검색" />
        <SearchPanel.Chips
          name="status"
          label="구분"
          options={POPUP_STATUS_OPTIONS}
          deselectTo="all"
        />
        <SearchPanel.QuickRange
          label="게시 기간"
          options={POPUP_QUICK_RANGE_OPTIONS}
        />
        <SearchPanel.DateRange />
      </SearchPanel>

      <AlertDiv>
        <p>
          [오늘 등록된 새 글 <P>{newPostsCount}건</P>] 검색 결과{" "}
          <P>{totalCount}건</P> 검색 되었습니다.
        </p>
        <ActionGroup>
          <SelectAllToggle
            totalCount={totalCount}
            selectedCount={selection.count}
            allSelected={selection.isAllSelected}
            onToggle={() =>
              selection.isAllSelected ? selection.clear() : selection.selectAll()
            }
          />
          <ActionButton
            onClick={handleEndButtonClick}
            disabled={selection.count === 0}
          >
            영구 삭제
          </ActionButton>
        </ActionGroup>
      </AlertDiv>

      <DataDiv>
        <DataTable
          columns={columns}
          data={pageItems}
          selectable
          selection={selection}
        />
      </DataDiv>
      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={setPage}
      />
    </BaseContainer>
  );
}

export default PopupManager;
